<template>
  <div
    class="relative rounded-r-md overflow-hidden h-5"
    style="background: linear-gradient(to right, orange 3%, green 100%)"
  >
    <div
      class="gradient-bar right-0 absolute bg-gray-300 rounded-r-md"
      :style="{width: `${100 - barWidth}%`}"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue';

const props = defineProps<{
    /** The audio stream to visualize */
    stream?: MediaStream | null;
}>();

const audioContext = ref<AudioContext | null>(null);
const analyser = ref<AnalyserNode | null>(null);
const dataArray = ref<Uint8Array | null>(null);
const barWidth = ref(0);
const animationFrameId = ref<number | null>(null);

/**
 * Setup the audio context.
 */
const setupAudioContext = (): void => {
    if (!props.stream) return;

    // Typescript doesn't recognize webkitAudioContext as a property of 'window' hence the type assertion
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    audioContext.value = new AudioContextClass();

    const source = audioContext.value.createMediaStreamSource(props.stream);
    analyser.value = audioContext.value.createAnalyser();
    source.connect(analyser.value);
    analyser.value.fftSize = 256;

    const bufferLength = analyser.value.frequencyBinCount;
    const buffer = new ArrayBuffer(bufferLength);
    dataArray.value = new Uint8Array(buffer);

    updateBar();
};

/**
 * Update the bar width.
 */
const updateBar = (): void => {
    if (!analyser.value || !dataArray.value) {
        return;
    }

    animationFrameId.value = requestAnimationFrame(updateBar);
    analyser.value.getByteFrequencyData(dataArray.value as Uint8Array<ArrayBuffer>);

    const sum = dataArray.value.reduce((a, b) => a + b, 0);
    const average = sum / dataArray.value.length;

    barWidth.value = (average / 255) * 100;
};

/**
 * Stop the audio context.
 */
const stopAudioContext = (): void => {
    if (animationFrameId.value !== null) {
        cancelAnimationFrame(animationFrameId.value);
        animationFrameId.value = null;
    }

    if (audioContext.value) {
        if (analyser.value) {
            analyser.value.disconnect();
            analyser.value = null;
        }
        dataArray.value = null;
        barWidth.value = 0;
        audioContext.value.close();
        audioContext.value = null;
    }
};

watch(
    () => props.stream,
    (newValue) => {
        if (newValue) {
            setupAudioContext();
        } else {
            stopAudioContext();
        }
    },
    { immediate: true }
);

onBeforeUnmount(() => {
    stopAudioContext();
});
</script>

<style scoped>
.gradient-bar {
    height: inherit;
    transition: width 0.1s ease-in-out, background-color 0.1s ease-in-out;
}
</style>
